import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';
import VendorsTabEnhanced from './VendorsTab';

const C = {
  bg:'#1a1c28', card:'#252838', card2:'#2a2d40', border:'#2e3150',
  text:'#e2e8f0', muted:'#64748b', accent:'#f97316', green:'#4ade80',
  red:'#f87171', blue:'#60a5fa', amber:'#fbbf24', purple:'#a78bfa',
};

const fmt = n => '$' + Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtDate = d => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';

function SpendOverview({ orders, vendors }) {
  // Spend by vendor
  const byVendor = {};
  orders.forEach(o => {
    const name = o.rental_vendors?.name || 'Unknown';
    if (!byVendor[name]) byVendor[name] = { name, total:0, received:0, pending:0, count:0 };
    byVendor[name].total += Number(o.total||0);
    byVendor[name].count++;
    if (o.status === 'received') byVendor[name].received += Number(o.total||0);
    else if (['pending','ordered'].includes(o.status)) byVendor[name].pending += Number(o.total||0);
  });
  const vendorRows = Object.values(byVendor).sort((a,b) => b.total - a.total);
  const maxSpend = vendorRows[0]?.total || 1;

  // Spend by month (last 12)
  const monthlySpend = {};
  for (let i = 11; i >= 0; i--) {
    const d = new Date(); d.setMonth(d.getMonth() - i);
    const key = d.toISOString().slice(0,7);
    monthlySpend[key] = 0;
  }
  orders.filter(o => o.status === 'received').forEach(o => {
    const key = (o.order_date||'').slice(0,7);
    if (monthlySpend[key] !== undefined) monthlySpend[key] += Number(o.total||0);
  });
  const monthlyData = Object.entries(monthlySpend);
  const maxMonthly = Math.max(...monthlyData.map(([,v])=>v), 1);

  const totalSpent = orders.filter(o=>o.status==='received').reduce((s,o)=>s+Number(o.total||0),0);
  const totalPending = orders.filter(o=>['pending','ordered'].includes(o.status)).reduce((s,o)=>s+Number(o.total||0),0);
  const totalOrders = orders.length;
  const activeVendors = new Set(orders.map(o=>o.vendor_id)).size;

  return (
    <div>
      {/* KPI cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
        {[
          ['Total Spent', fmt(totalSpent), C.accent],
          ['Outstanding POs', fmt(totalPending), C.amber],
          ['Total Orders', totalOrders, C.blue],
          ['Active Vendors', activeVendors, C.green],
        ].map(([l,v,c]) => (
          <div key={l} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'16px 20px' }}>
            <div style={{ fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{l}</div>
            <div style={{ fontSize:22, fontWeight:800, color:c }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Spend over time chart */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'20px 24px', marginBottom:20 }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>Spend Over Time (Last 12 Months)</div>
        <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:140 }}>
          {monthlyData.map(([month, amount]) => {
            const pct = amount / maxMonthly;
            const label = new Date(month+'-01').toLocaleDateString('en-US',{month:'short'});
            return (
              <div key={month} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                <div style={{ fontSize:9, color:C.muted }}>{amount > 0 ? fmt(amount).replace('$','$') : ''}</div>
                <div style={{ width:'100%', background: amount > 0 ? C.accent : C.border, borderRadius:'4px 4px 0 0', height: Math.max(4, pct * 100) + 'px', transition:'height 0.3s' }} />
                <div style={{ fontSize:9, color:C.muted }}>{label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Spend by vendor */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'20px 24px' }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>Spend by Vendor</div>
        {vendorRows.length === 0 ? (
          <div style={{ textAlign:'center', padding:40, color:C.muted, fontSize:13 }}>No purchase orders yet</div>
        ) : vendorRows.map(v => (
          <div key={v.name} style={{ marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{v.name}</div>
              <div style={{ fontSize:13, fontWeight:700, color:C.accent }}>{fmt(v.total)}</div>
            </div>
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <div style={{ flex:1, background:C.card2, borderRadius:4, height:8, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${(v.total/maxSpend)*100}%`, background:C.accent, borderRadius:4 }} />
              </div>
              <div style={{ fontSize:11, color:C.green, minWidth:60, textAlign:'right' }}>{fmt(v.received)} received</div>
              {v.pending > 0 && <div style={{ fontSize:11, color:C.amber, minWidth:60, textAlign:'right' }}>{fmt(v.pending)} pending</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PurchaseOrdersTab() {
  const [orders, setOrders] = React.useState([]);
  const [vendors, setVendors] = React.useState([]);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [vendorSearch, setVendorSearch] = React.useState('');
  const [showVendorDrop, setShowVendorDrop] = React.useState(false);
  const blank = { vendor_id:'', vendor_name:'', po_number:'', order_date:new Date().toISOString().split('T')[0], received_date:'', status:'pending', subtotal:0, tax:0, total:0, notes:'' };
  const [form, setForm] = React.useState(blank);
  const [lineItems, setLineItems] = React.useState([]);
  const newLine = { description:'', quantity:1, unit_cost:0, rental_item_id:'' };
  const [newLI, setNewLI] = React.useState(newLine);
  const STATUS_COLOR = { pending:'#fbbf24', ordered:'#60a5fa', received:'#34d399', cancelled:'#f87171' };

  const inp = { padding:'8px 10px', borderRadius:6, border:`1px solid ${C.border}`, background:'#1e2030', color:C.text, fontSize:12, width:'100%', boxSizing:'border-box' };

  const load = async () => {
    setLoading(true);
    const [po, vend, inv] = await Promise.all([
      axios.get('/api/rental-assets/po'),
      axios.get('/api/rental-assets/vendors'),
      axios.get('/api/rentals/items'),
    ]);
    setOrders(po.data.orders || []);
    setVendors(vend.data.vendors || []);
    setItems(inv.data.items || []);
    setLoading(false);
  };

  React.useEffect(() => { load(); }, []);

  const recalc = (lis) => {
    const sub = lis.reduce((s,l) => s + (Number(l.quantity||1) * Number(l.unit_cost||0)), 0);
    setForm(p => ({ ...p, subtotal: sub, total: sub + Number(p.tax||0) }));
  };

  const addLine = () => {
    if (!newLI.description) return;
    const updated = [...lineItems, { ...newLI, id: Date.now() }];
    setLineItems(updated);
    recalc(updated);
    setNewLI(newLine);
  };

  const removeLine = (id) => {
    const updated = lineItems.filter(l => l.id !== id);
    setLineItems(updated);
    recalc(updated);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post('/api/rental-assets/po', { ...form, items: lineItems });
      setShowForm(false); setForm(blank); setLineItems([]);
      await load();
    } catch(e) { alert('Error saving PO'); }
    setSaving(false);
  };

  const updateStatus = async (id, status) => {
    await axios.patch('/api/rental-assets/po/' + id, { status });
    setOrders(p => p.map(o => o.id === id ? { ...o, status } : o));
  };

  const deletePO = async (id) => {
    if (!window.confirm('Delete this purchase order?')) return;
    await axios.delete('/api/rental-assets/po/' + id);
    setOrders(p => p.filter(o => o.id !== id));
  };

  const filteredVendors = vendors.filter(v => !vendorSearch || v.name.toLowerCase().includes(vendorSearch.toLowerCase()));

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text }}>Purchase Orders <span style={{ color:C.muted, fontWeight:400 }}>({orders.length})</span></div>
        <button onClick={()=>{ setShowForm(true); setForm(blank); setLineItems([]); }}
          style={{ padding:'8px 18px', borderRadius:8, border:'none', background:C.accent, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>+ New PO</button>
      </div>

      {/* Summary cards */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
        {[
          ['Total POs', orders.length, C.text],
          ['Pending', orders.filter(o=>o.status==='pending').length, '#fbbf24'],
          ['Ordered', orders.filter(o=>o.status==='ordered').length, '#60a5fa'],
          ['Received', orders.filter(o=>o.status==='received').length, '#34d399'],
          ['Total Spent', fmt(orders.filter(o=>o.status==='received').reduce((s,o)=>s+Number(o.total||0),0)), C.accent],
        ].map(([l,v,c]) => (
          <div key={l} style={{ background:C.card2, border:`1px solid ${C.border}`, borderRadius:10, padding:'10px 16px', flex:1, minWidth:100 }}>
            <div style={{ fontSize:9, color:C.muted, textTransform:'uppercase', letterSpacing:1, marginBottom:3 }}>{l}</div>
            <div style={{ fontSize:16, fontWeight:800, color:c }}>{v}</div>
          </div>
        ))}
      </div>

      {/* New PO Form */}
      {showForm && (
        <div style={{ background:C.card2, border:`1px solid ${C.border}`, borderRadius:12, padding:20, marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{ fontSize:14, fontWeight:700, color:C.text }}>New Purchase Order</div>
            <button onClick={()=>setShowForm(false)} style={{ background:'none', border:'none', color:C.muted, fontSize:20, cursor:'pointer' }}>×</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:16 }}>
            {/* Vendor searchable dropdown */}
            <div style={{ position:'relative' }}>
              <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase' }}>Vendor</div>
              <input value={vendorSearch || form.vendor_name} onChange={e=>{ setVendorSearch(e.target.value); setShowVendorDrop(true); }}
                onFocus={()=>setShowVendorDrop(true)} onBlur={()=>setTimeout(()=>setShowVendorDrop(false),200)}
                placeholder="Search vendor..." style={inp} />
              {showVendorDrop && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, background:C.card, border:`1px solid ${C.border}`, borderRadius:8, zIndex:100, maxHeight:180, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
                  {filteredVendors.map(v => (
                    <div key={v.id} onClick={()=>{ setForm(p=>({...p,vendor_id:v.id,vendor_name:v.name})); setVendorSearch(''); setShowVendorDrop(false); }}
                      style={{ padding:'9px 12px', cursor:'pointer', borderBottom:`1px solid ${C.border}`, fontSize:13, color:C.text }}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(249,115,22,0.08)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <div style={{ fontWeight:600 }}>{v.name}</div>
                      {v.category && <div style={{ fontSize:10, color:C.muted }}>{v.category}</div>}
                    </div>
                  ))}
                  {filteredVendors.length === 0 && <div style={{ padding:12, color:C.muted, fontSize:12 }}>No vendors found</div>}
                </div>
              )}
              {form.vendor_name && !vendorSearch && <div style={{ fontSize:11, color:C.green, marginTop:3 }}>✓ {form.vendor_name}</div>}
            </div>
            <div>
              <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase' }}>PO Number</div>
              <input value={form.po_number} onChange={e=>setForm(p=>({...p,po_number:e.target.value}))} style={inp} placeholder='PO-2026-001' />
            </div>
            <div>
              <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase' }}>Order Date</div>
              <input type='date' value={form.order_date} onChange={e=>setForm(p=>({...p,order_date:e.target.value}))} style={inp} />
            </div>
            <div>
              <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase' }}>Status</div>
              <select value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))} style={inp}>
                {['pending','ordered','received','cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase' }}>Expected Delivery</div>
              <input type='date' value={form.received_date} onChange={e=>setForm(p=>({...p,received_date:e.target.value}))} style={inp} />
            </div>
            <div>
              <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase' }}>Notes</div>
              <input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} style={inp} placeholder='Optional...' />
            </div>
          </div>

          {/* Line items */}
          <div style={{ fontSize:11, fontWeight:700, color:C.muted, marginBottom:8, textTransform:'uppercase' }}>Line Items</div>
          {lineItems.length > 0 && (
            <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:10, fontSize:12 }}>
              <thead><tr style={{ background:'#1e2235' }}>
                {['Description','Item','Qty','Unit Cost','Total',''].map(h => <th key={h} style={{ padding:'6px 8px', textAlign:'left', fontSize:10, color:C.muted, textTransform:'uppercase' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {lineItems.map(l => (
                  <tr key={l.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                    <td style={{ padding:'8px' }}><span style={{ color:C.text }}>{l.description}</span></td>
                    <td style={{ padding:'8px' }}><span style={{ color:C.muted }}>{items.find(i=>i.id==l.rental_item_id)?.name||'—'}</span></td>
                    <td style={{ padding:'8px' }}><span style={{ color:C.text }}>{l.quantity}</span></td>
                    <td style={{ padding:'8px' }}><span style={{ color:C.text }}>{fmt(l.unit_cost)}</span></td>
                    <td style={{ padding:'8px' }}><span style={{ color:C.accent, fontWeight:700 }}>{fmt(Number(l.quantity)*Number(l.unit_cost))}</span></td>
                    <td style={{ padding:'8px' }}><button onClick={()=>removeLine(l.id)} style={{ background:'none', border:'none', color:C.red, cursor:'pointer' }}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr auto', gap:8, marginBottom:12, alignItems:'end' }}>
            <div>
              <div style={{ fontSize:10, color:C.muted, marginBottom:3 }}>Description</div>
              <input value={newLI.description} onChange={e=>setNewLI(p=>({...p,description:e.target.value}))} style={inp} placeholder='Part, service, or item...' />
            </div>
            <div>
              <div style={{ fontSize:10, color:C.muted, marginBottom:3 }}>Inventory Item</div>
              <select value={newLI.rental_item_id} onChange={e=>setNewLI(p=>({...p,rental_item_id:e.target.value}))} style={inp}>
                <option value=''>None</option>
                {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:10, color:C.muted, marginBottom:3 }}>Qty</div>
              <input type='number' min='1' value={newLI.quantity} onChange={e=>setNewLI(p=>({...p,quantity:e.target.value}))} style={inp} />
            </div>
            <div>
              <div style={{ fontSize:10, color:C.muted, marginBottom:3 }}>Unit Cost</div>
              <input type='number' min='0' step='0.01' value={newLI.unit_cost} onChange={e=>setNewLI(p=>({...p,unit_cost:e.target.value}))} style={inp} />
            </div>
            <button onClick={addLine} style={{ padding:'8px 14px', borderRadius:6, border:'none', background:C.accent, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>+ Add</button>
          </div>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:12, borderTop:`1px solid ${C.border}` }}>
            <div style={{ display:'flex', gap:16, fontSize:13 }}>
              <span style={{ color:C.muted }}>Subtotal: <strong style={{ color:C.text }}>{fmt(form.subtotal)}</strong></span>
              <span style={{ color:C.muted }}>Tax: <input type='number' value={form.tax} onChange={e=>{ const t=Number(e.target.value); setForm(p=>({...p,tax:t,total:p.subtotal+t})); }} style={{ ...inp, width:70, display:'inline-block' }} /></span>
              <span style={{ color:C.muted }}>Total: <strong style={{ color:C.accent, fontSize:15 }}>{fmt(form.total)}</strong></span>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setShowForm(false)} style={{ padding:'8px 16px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, fontSize:13, cursor:'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ padding:'8px 20px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', opacity:saving?0.6:1 }}>
                {saving ? 'Saving...' : 'Save PO'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PO Table */}
      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:C.muted }}>Loading...</div>
      ) : orders.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, color:C.muted, fontSize:14 }}>No purchase orders yet</div>
      ) : (
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${C.border}` }}>
              {['PO #','Vendor','Date','Items','Total','Status',''].map(h => (
                <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o.id} style={{ borderBottom:`1px solid ${C.border}` }}
                onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={{ padding:'10px 12px', fontSize:12, fontWeight:700, color:C.blue }}>{o.po_number||'#'+o.id}</td>
                <td style={{ padding:'10px 12px', fontSize:12, color:C.text }}>{o.rental_vendors?.name||'—'}</td>
                <td style={{ padding:'10px 12px', fontSize:12, color:C.muted }}>{fmtDate(o.order_date)}</td>
                <td style={{ padding:'10px 12px', fontSize:12, color:C.muted }}>{(o.rental_po_items||[]).length} items</td>
                <td style={{ padding:'10px 12px', fontSize:13, fontWeight:700, color:C.accent }}>{fmt(o.total)}</td>
                <td style={{ padding:'10px 12px' }}>
                  <select value={o.status} onChange={e=>updateStatus(o.id,e.target.value)}
                    style={{ background:'transparent', border:'none', fontSize:11, fontWeight:700, cursor:'pointer', outline:'none', color:STATUS_COLOR[o.status] }}>
                    {['pending','ordered','received','cancelled'].map(s => <option key={s} value={s} style={{ background:'#252838', color:C.text }}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                  </select>
                </td>
                <td style={{ padding:'10px 12px' }}>
                  <button onClick={()=>deletePO(o.id)} style={{ background:'none', border:'none', color:C.red, cursor:'pointer', fontSize:12 }}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function SupplyChainView() {
  const [tab, setTab] = useState('vendors');
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    axios.get('/api/rental-assets/po').then(r => setOrders(r.data.orders || [])).catch(()=>{});
    axios.get('/api/rental-assets/vendors').then(r => setVendors(r.data.vendors || [])).catch(()=>{});
  }, []);

  const tabs = [['vendors','🏭 Vendors'],['po','📄 Purchase Orders'],['spend','📊 Spend Overview']];

  return (
    <div style={{ fontFamily:"'DM Sans',Arial,sans-serif", padding:'24px 28px' }}>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:22, fontWeight:800, color:C.text, marginBottom:4 }}>🔗 Supply Chain</div>
        <div style={{ fontSize:13, color:C.muted }}>Manage vendors, purchase orders, and spending</div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:24, borderBottom:`1px solid ${C.border}` }}>
        {tabs.map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)}
            style={{ padding:'10px 20px', border:'none', borderBottom: tab===v ? `2px solid ${C.accent}` : '2px solid transparent', background:'transparent', color: tab===v ? C.accent : C.muted, fontWeight: tab===v ? 700 : 400, fontSize:13, cursor:'pointer' }}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'vendors' && <VendorsTabEnhanced axios={axios} />}
      {tab === 'po' && <PurchaseOrdersTab />}
      {tab === 'spend' && <SpendOverview orders={orders} vendors={vendors} />}
    </div>
  );
}
